// ============================================
// Zen-AI-Pentest Platform - Dashboard Page
// ============================================

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Scan,
  ShieldAlert,
  Bot,
  FileText,
  TrendingUp,
  TrendingDown,
  Activity,
  Zap,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { dashboardApi } from '@/services/api';
import { useDashboardStore, useScanStore } from '@/store';
import { ScanTrendChart, VulnerabilityTrendChart } from '@/components/charts/DashboardCharts';
import { NetworkGraphPreview } from '@/components/graphs/NetworkGraphPreview';
import { RecentActivity } from '@/components/custom/RecentActivity';
import { Link } from 'react-router-dom';

// ============================================
// Stat Card Component
// ============================================
interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ElementType;
  trend?: { value: number; positive: boolean };
  color: string;
  link?: string;
}

function StatCard({ title, value, description, icon: Icon, trend, color, link }: StatCardProps) {
  const content = (
    <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
      <div className={`absolute top-0 right-0 w-24 h-24 ${color} opacity-10 rounded-bl-full`} />
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
          <Icon className={`w-4 h-4 ${color.replace('bg-', 'text-')}`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        {trend && (
          <div className={`flex items-center mt-2 text-xs ${trend.positive ? 'text-green-500' : 'text-red-500'}`}>
            {trend.positive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
            {trend.value}%
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (link) {
    return <Link to={link} className="block">{content}</Link>;
  }

  return content;
}

// ============================================
// Active Scans Component
// ============================================
function ActiveScans() {
  const { activeScans, scans } = useScanStore();
  const activeScanData = scans.filter((s) => activeScans.includes(s.id));

  if (activeScanData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Active Scans
          </CardTitle>
          <CardDescription>No active scans at the moment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Scan className="w-12 h-12 mx-auto text-muted-foreground opacity-50 mb-4" />
            <p className="text-muted-foreground">Start a new scan to see it here</p>
            <Button asChild className="mt-4">
              <Link to="/scans/new">Start Scan</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Active Scans
        </CardTitle>
        <CardDescription>{activeScanData.length} scan(s) in progress</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeScanData.map((scan) => (
          <div key={scan.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{scan.name}</p>
                <p className="text-sm text-muted-foreground">{scan.target}</p>
              </div>
              <Badge variant="outline">{scan.status}</Badge>
            </div>
            <Progress value={scan.progress} className="h-2" />
            <p className="text-xs text-muted-foreground text-right">{scan.progress}%</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ============================================
// Vulnerability Summary Component
// ============================================
function VulnerabilitySummary() {
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
  });

  const severityCounts = stats?.totalVulnerabilities
    ? {
        critical: stats.criticalVulnerabilities || 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
      }
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5" />
          Vulnerability Summary
        </CardTitle>
        <CardDescription>Overview of detected vulnerabilities</CardDescription>
      </CardHeader>
      <CardContent>
        {severityCounts ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm">Critical</span>
              </div>
              <Badge variant="destructive">{severityCounts.critical}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <span className="text-sm">High</span>
              </div>
              <Badge className="bg-orange-500">{severityCounts.high}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <span className="text-sm">Medium</span>
              </div>
              <Badge className="bg-yellow-500">{severityCounts.medium}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500" />
                <span className="text-sm">Low</span>
              </div>
              <Badge className="bg-blue-500">{severityCounts.low}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-gray-500" />
                <span className="text-sm">Info</span>
              </div>
              <Badge variant="secondary">{severityCounts.info}</Badge>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No vulnerability data available
          </div>
        )}
        <Button asChild variant="outline" className="w-full mt-4">
          <Link to="/vulnerabilities">View All Vulnerabilities</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================
// Main Dashboard Page
// ============================================
export function DashboardPage() {
  const { setStats } = useDashboardStore();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  useEffect(() => {
    if (stats) {
      setStats(stats);
    }
  }, [stats, setStats]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back! Here's what's happening with your security posture.
          </p>
        </div>
        <Button asChild>
          <Link to="/scans/new">
            <Zap className="w-4 h-4 mr-2" />
            New Scan
          </Link>
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Scans"
          value={stats?.totalScans || 0}
          description={`${stats?.activeScans || 0} active`}
          icon={Scan}
          trend={{ value: 12, positive: true }}
          color="bg-blue-500"
          link="/scans"
        />
        <StatCard
          title="Vulnerabilities"
          value={stats?.totalVulnerabilities || 0}
          description={`${stats?.criticalVulnerabilities || 0} critical`}
          icon={ShieldAlert}
          trend={{ value: 5, positive: false }}
          color="bg-red-500"
          link="/vulnerabilities"
        />
        <StatCard
          title="Agents"
          value={stats?.totalAgents || 0}
          description={`${stats?.onlineAgents || 0} online`}
          icon={Bot}
          trend={{ value: 8, positive: true }}
          color="bg-green-500"
          link="/agents"
        />
        <StatCard
          title="Reports"
          value="12"
          description="Generated this month"
          icon={FileText}
          trend={{ value: 15, positive: true }}
          color="bg-purple-500"
          link="/reports"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Charts */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Scan Activity</CardTitle>
              <CardDescription>Number of scans over the last 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              <ScanTrendChart />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Vulnerability Trends</CardTitle>
              <CardDescription>Vulnerability discoveries over time</CardDescription>
            </CardHeader>
            <CardContent>
              <VulnerabilityTrendChart />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Network Topology</CardTitle>
              <CardDescription>Visual representation of your infrastructure</CardDescription>
            </CardHeader>
            <CardContent>
              <NetworkGraphPreview />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <ActiveScans />
          <VulnerabilitySummary />
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RecentActivity />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Compliance Score</CardTitle>
              <CardDescription>Overall compliance status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-4">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-muted"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(stats?.complianceScore || 0) * 3.52} 351.86`}
                      className="text-green-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold">{stats?.complianceScore || 0}%</span>
                  </div>
                </div>
              </div>
              <Button asChild variant="outline" className="w-full">
                <Link to="/compliance">View Compliance</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
